import type Highcharts from "highcharts";
import type { ToggleableChart } from "../types";
import { getChartEntry, getConvertibleTargets } from "../registry";

// ---------------------------------------------------------------------------
// Helpers — create onclick handlers for the Highcharts context menu.
// Highcharts calls onclick with `this = chart`, so we cast to ToggleableChart
// (which adds setChartType / convertToSourceChart methods set up in ChartFactory).
// ---------------------------------------------------------------------------

function createSetChartTypeFn(targetType: string) {
  return function (this: Highcharts.Chart) {
    (this as ToggleableChart).setChartType?.(targetType);
  };
}

function createConvertToSourceFn() {
  return function (this: Highcharts.Chart) {
    (this as ToggleableChart).convertToSourceChart?.();
  };
}

function getConversionLabel(fromType: string, toType: string): string {
  if (toType === "table") {
    // These two chart types show their data in a companion table, not a converted one.
    if (fromType === "tiered_bar" || fromType === "quadrant_scatter")
      return "View data table";
    return "Convert to table";
  }
  if (toType === "pie") return "Convert to pie chart";
  if (toType === "bar") return "Convert to bar chart";
  if (toType === "funnel") return "Convert to funnel chart";
  return `Convert to ${toType}`;
}

type MenuConfig = {
  menuItemDefinitions: Record<string, { text: string; onclick: (this: Highcharts.Chart) => void }>;
  menuItems: string[];
};

// ---------------------------------------------------------------------------
// buildContextMenu — builds the Highcharts hamburger menu for any chart view.
//
// Three clear states:
//   1. Viewing original chart  → show export items + what this chart can convert to (registry-driven)
//   2. funnel converted to bar → only allow converting back to funnel
//   3. Any other conversion    → allow bar↔pie cross-toggle, convert to table, go back to source
// ---------------------------------------------------------------------------
export function buildContextMenu(
  activeType: string,   // chart type currently rendered
  sourceType: string,   // chart type before any conversion (used to know "where to go back")
  isConverted: boolean, // true when activeType differs from the original source chart
): MenuConfig {
  const entry = getChartEntry(activeType);

  // Export items (PNG, CSV, etc.) always come from the active chart's registry definition.
  const exportItems = entry?.exportItems ?? [
    "viewFullscreen",
    "printChart",
    "downloadPNG",
    "downloadJPEG",
    "downloadCSV",
    "downloadXLS",
  ];

  const defs: MenuConfig["menuItemDefinitions"] = {};
  const convKeys: string[] = [];

  // --- State 1: Not converted. Show what this chart type can convert to. ---
  if (!isConverted) {
    for (const target of getConvertibleTargets(activeType)) {
      const key = `convertTo_${target}`;
      defs[key] = {
        text: getConversionLabel(activeType, target),
        onclick: createSetChartTypeFn(target),
      };
      convKeys.push(key);
    }
    return { menuItemDefinitions: defs, menuItems: [...exportItems, ...convKeys] };
  }

  // --- State 2: funnel converted to bar. Only "go back to funnel" makes sense here. ---
  if (sourceType === "funnel" && activeType === "bar") {
    defs.convertToSourceChart = {
      text: "Convert to funnel chart",
      onclick: createConvertToSourceFn(),
    };
    return { menuItemDefinitions: defs, menuItems: [...exportItems, "convertToSourceChart"] };
  }

  // --- State 3: Any other conversion (e.g. map → bar, bar → pie, pie_drilldown → bar). ---
  // Allow cross-toggle between bar and pie, plus a "convert to table" and "go back" option.
  if (activeType === "bar") {
    defs.toggleChartType = {
      text: "Convert to pie chart",
      onclick: createSetChartTypeFn("pie"),
    };
    convKeys.push("toggleChartType");
  } else if (activeType === "pie") {
    defs.convertToBar = {
      text: "Convert to bar chart",
      onclick: createSetChartTypeFn("bar"),
    };
    convKeys.push("convertToBar");
  }

  defs.convertToTable = {
    text: "Convert to table",
    onclick: createSetChartTypeFn("table"),
  };
  convKeys.push("convertToTable");

  defs.convertToSourceChart = {
    text: "Go to source chart",
    onclick: createConvertToSourceFn(),
  };
  convKeys.push("convertToSourceChart");

  return { menuItemDefinitions: defs, menuItems: [...exportItems, ...convKeys] };
}

